import { categoriesSelectors } from 'app-components/flux/categories';
import type { ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import type { ScheduledTransactionEditSeriesType as SeriesType } from 'app-components/flux/scheduled-transactions/scheduledTransactionEditForm';
import {
  deriveSeriesTypeChange,
  type RecurringSeriesType,
} from 'app-components/flux/scheduled-transactions/seriesTypeChangeUtils';

type AccountExcludeDefaults = {
  excludeFromSpendingPlan: boolean;
  excludeFromReports: boolean;
};

type TxnLike = {
  coa?: ChartOfAccountProps | null;
  amount?: number | '' | null;
};

export type { SeriesType };

/**
 * Applies the shared series-type-change ruleset (`deriveSeriesTypeChange`) to the legacy dialog's formik state.
 * Dest-side exclusions reset on every change; source-side exclusions only change when the transfer boundary is
 * crossed, so user-set exclusions survive bill/subscription/income moves.
 */
export function applySeriesTypeChange(args: {
  nextType: SeriesType;
  currentType: SeriesType;
  create: boolean;
  currentTxn: TxnLike;
  hasSplitItems: boolean;
  incomeCategoryId?: string | null;
  accountExcludeDefaults: AccountExcludeDefaults;
  setFieldValue: (path: string, value: unknown) => unknown;
}): void {
  const {
    nextType,
    currentType,
    create,
    currentTxn,
    hasSplitItems,
    incomeCategoryId,
    accountExcludeDefaults,
    setFieldValue,
  } = args;

  if (currentType === nextType) return;

  const currentCoa = currentTxn?.coa ?? null;
  const rawAmount = currentTxn?.amount ?? 0;
  const currentAmount = typeof rawAmount === 'number' ? rawAmount : Number(rawAmount) || 0;
  const coaIsIncomeCategory = Boolean(
    currentCoa?.type === 'CATEGORY' && categoriesSelectors.isIncomeCat(null, currentCoa.id),
  );

  const change = deriveSeriesTypeChange({
    prevType: currentType.toLowerCase(),
    nextType: nextType.toLowerCase() as RecurringSeriesType,
    createLike: create,
    coa: currentCoa,
    amount: currentAmount,
    hasSplitItems,
    coaIsIncomeCategory,
    incomeCategoryId,
    accountExcludeDefaults,
  });

  setFieldValue('transaction.isBill', nextType === 'BILL');
  setFieldValue('transaction.isSubscription', nextType === 'SUBSCRIPTION');
  setFieldValue('transaction.isIncome', nextType === 'INCOME');
  setFieldValue('transaction.isTransfer', nextType === 'TRANSFER');
  setFieldValue('transaction.excludeF2SDest', change.destExclusions.excludeF2SDest);
  setFieldValue('transaction.excludeReportsDest', change.destExclusions.excludeReportsDest);
  if (change.sourceExclusions) {
    setFieldValue('transaction.isExcludedFromF2S', change.sourceExclusions.isExcludedFromF2S);
    setFieldValue('transaction.isExcludedFromReports', change.sourceExclusions.isExcludedFromReports);
  }
  if (change.coa) {
    setFieldValue('transaction.coa', change.coa);
  }
  if (change.amount !== undefined) {
    setFieldValue('transaction.amount', change.amount);
  }
}
